/**
 * 文件路径: D:\XXY\MeiShan_Project\frontend\shared\api\menuTags.ts
 * 菜单标签 API 封装
 * 
 * 功能：
 * - 菜单标签列表查询（支持筛选、搜索、分页）
 * - 菜单标签创建
 * - 菜单标签详情查询
 * - 菜单标签更新
 * - 菜单标签删除
 * 
 * 依赖：
 * - httpClient: HTTP 请求客户端
 * - MenuTag, MenuTagWrite: 自动生成的类型定义
 */

import { httpClient } from './http'
import type { MenuTag } from '../generated'

export type { MenuTag }

/**
 * 菜单标签创建/更新请求类型
 * 注意：operator_tenant 由后端根据当前用户自动注入，前端无需传递
 * merchant：运营商管理员可指定商户（创建商户专属标签），留空为全局标签；
 *           商户管理员由后端自动覆盖为当前商户，前端无需传递
 */
export interface MenuTagRequest {
  name: string
  color?: string
  description?: string | null
  /** 所属商户ID（运营商端可选，留空为全局标签；商户端由后端自动注入） */
  merchant?: number | string | null
}

/**
 * 菜单标签列表查询参数
 */
export interface MenuTagListParams {
  /** 页码（1-based） */
  page?: number
  /** 每页数量 */
  page_size?: number
  /** 运营商ID（多租户过滤） */
  operator_tenant?: string | number
  /** 商户ID（过滤商户专属标签） */
  merchant?: string | number
  /** 搜索关键词（标签名称、描述） */
  search?: string
  /** 排序字段 */
  ordering?: string
}

/**
 * 菜单标签分页响应
 */
export interface MenuTagListResponse {
  count: number
  next: string | null
  previous: string | null
  results: MenuTag[]
}

/**
 * 菜单标签 API
 */
export const menuTagsApi = {
  /**
   * 获取菜单标签列表
   * 
   * @param params 查询参数
   * @returns 菜单标签列表
   * 
   * @example
   * ```typescript
   * // 查询全局标签
   * const tags = await menuTagsApi.list({ merchant: null })
   * 
   * // 查询商户专属标签
   * const tags = await menuTagsApi.list({ merchant: '123' })
   * 
   * // 搜索标签
   * const tags = await menuTagsApi.list({ search: '轻食' })
   * ```
   */
  async list(params?: MenuTagListParams): Promise<MenuTagListResponse> {
    const response = await httpClient.get<MenuTagListResponse>(
      'merchants/menu-tags/',
      { params }
    )
    return response
  },

  /**
   * 获取菜单标签详情
   * 
   * @param id 标签ID
   * @returns 菜单标签详情
   * 
   * @example
   * ```typescript
   * const tag = await menuTagsApi.get('1')
   * console.log(tag.name, tag.color, tag.owner_display)
   * ```
   */
  async get(id: string | number): Promise<MenuTag> {
    const response = await httpClient.get<MenuTag>(
      `merchants/menu-tags/${id}/`
    )
    return response
  },

  /**
   * 创建菜单标签
   * 
   * @param data 标签数据
   * @returns 创建的标签
   * 
   * @example
   * ```typescript
   * // 创建全局标签
   * const tag = await menuTagsApi.create({
   *   name: '荤素搭配',
   *   color: '#67C23A',
   *   description: '荤素搭配，营养均衡',
   *   operator_tenant: 1
   * })
   * 
   * // 创建商户专属标签
   * const tag = await menuTagsApi.create({
   *   name: '招牌菜',
   *   color: '#F56C6C',
   *   description: '本店招牌菜',
   *   operator_tenant: 1,
   *   merchant: 123
   * })
   * ```
   */
  async create(data: MenuTagRequest): Promise<MenuTag> {
    const response = await httpClient.post<MenuTag>(
      'merchants/menu-tags/',
      data
    )
    return response
  },

  /**
   * 更新菜单标签
   * 
   * @param id 标签ID
   * @param data 更新数据（部分字段）
   * @returns 更新后的标签
   * 
   * @example
   * ```typescript
   * const tag = await menuTagsApi.update('1', {
   *   name: '荤素搭配（推荐）',
   *   color: '#409EFF'
   * })
   * ```
   */
  async update(id: string | number, data: Partial<MenuTagRequest>): Promise<MenuTag> {
    const response = await httpClient.patch<MenuTag>(
      `merchants/menu-tags/${id}/`,
      data
    )
    return response
  },

  /**
   * 删除菜单标签
   * 
   * @param id 标签ID
   * 
   * @example
   * ```typescript
   * await menuTagsApi.delete('1')
   * ```
   * 
   * @note
   * - 如果标签被菜单使用，后端会返回错误
   * - 前端应该先检查使用情况，再提示用户确认删除
   */
  async delete(id: string | number): Promise<void> {
    await httpClient.delete(`merchants/menu-tags/${id}/`)
  },

  /**
   * 批量获取菜单标签（不分页）
   * 
   * @param params 查询参数
   * @returns 所有匹配的标签
   * 
   * @example
   * ```typescript
   * // 获取所有全局标签（用于选择器）
   * const tags = await menuTagsApi.all({ merchant: null })
   * ```
   */
  async all(params?: Omit<MenuTagListParams, 'page' | 'page_size'>): Promise<MenuTag[]> {
    const response = await httpClient.get<MenuTagListResponse>(
      'merchants/menu-tags/',
      {
        params: {
          ...params,
          page_size: 1000 // 获取所有数据
        }
      }
    )
    return response.results
  },

  /**
   * 检查标签是否被使用
   * 
   * @param id 标签ID
   * @returns 使用情况
   * 
   * @example
   * ```typescript
   * const usage = await menuTagsApi.checkUsage('1')
   * if (usage.menu_count > 0) {
   *   ElMessageBox.confirm(
   *     `该标签正在被 ${usage.menu_count} 个菜单使用，删除后无法恢复，是否继续？`,
   *     '确认删除'
   *   )
   * }
   * ```
   * 
   * @note
   * 此接口需要后端支持，如果后端未实现，可以省略或在删除时依赖后端错误提示
   */
  async checkUsage(id: string | number): Promise<{ menu_count: number }> {
    void id
    // TODO: 后端需要实现此接口
    // const response = await httpClient.get<{ menu_count: number }>(
    //   `merchants/menu-tags/${id}/usage/`
    // )
    // return response
    
    // 暂时返回模拟数据
    return { menu_count: 0 }
  }
}

/**
 * 预设颜色（用于颜色选择器）
 */
export const PRESET_TAG_COLORS = [
  '#409EFF', // 品牌蓝
  '#67C23A', // 成功绿
  '#E6A23C', // 警告黄
  '#F56C6C', // 危险红
  '#909399', // 信息灰
  '#00D7FF', // 青色
  '#FF69B4', // 粉色
  '#9370DB', // 紫色
  '#FF8C00', // 橙色
  '#2E8B57'  // 深绿
]

/**
 * 标签颜色工具函数
 */
export const tagColorUtils = {
  /**
   * 获取颜色的对比色（用于文字颜色）
   * 
   * @param color 背景颜色（十六进制）
   * @returns 文字颜色（黑色或白色）
   */
  getContrastColor(color: string): string {
    // 移除 # 号
    const hex = color.replace('#', '')
    
    // 转换为 RGB
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    
    // 计算亮度（YIQ 公式）
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000
    
    // 亮度大于 128 使用黑色文字，否则使用白色文字
    return yiq >= 128 ? '#000000' : '#FFFFFF'
  },

  /**
   * 验证颜色格式
   * 
   * @param color 颜色值
   * @returns 是否为有效的十六进制颜色
   */
  isValidColor(color: string): boolean {
    return /^#([0-9A-F]{3}){1,2}$/i.test(color)
  },

  /**
   * 获取随机颜色
   * 
   * @returns 随机预设颜色
   */
  getRandomColor(): string {
    return PRESET_TAG_COLORS[Math.floor(Math.random() * PRESET_TAG_COLORS.length)]
  },

  /**
   * 获取文字颜色（兼容旧调用方）
   *
   * @param color 背景颜色
   * @returns 文字颜色
   */
  getTextColor(color: string): string {
    return tagColorUtils.getContrastColor(color)
  }
}
